import { useEffect, useState } from "react";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/** Dialog pindah marking ke SPK vendor lain: pilih SPK tujuan (proyek sama) ATAU vendor baru (SPK baru dibuat otomatis). */
export function MoveMarkingDialog({ spk, marks, onClose, onMoved }) {
  const [mode, setMode] = useState("spk");
  const [spks, setSpks] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [targetSpk, setTargetSpk] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [rateTon, setRateTon] = useState(spk?.rateTon || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/spk?projectId=${spk.projectId}`).then((r) => {
      const others = r.data.filter((s) => s.id !== spk.id && !["Digantikan", "Selesai"].includes(s.status));
      setSpks(others); if (!others.length) setMode("vendor");
    }).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data.filter((v) => v.id !== spk.vendorId))).catch(() => {});
  }, [spk]);
  const locked = marks.filter((m) => (m.stb || 0) > 0 || (m.delivery || 0) > 0);
  const movable = marks.filter((m) => !locked.includes(m));
  const submit = async () => {
    if (!movable.length) { toast.error("Tidak ada marking yang bisa dipindah"); return; }
    if (mode === "spk" && !targetSpk) { toast.error("Pilih SPK tujuan"); return; }
    if (mode === "vendor" && !vendorId) { toast.error("Pilih vendor tujuan"); return; }
    setBusy(true);
    try {
      const body = { markingIds: movable.map((m) => m.id), ...(mode === "spk" ? { targetSpkId: targetSpk } : { vendorId, tanggal, rateTon: Number(rateTon) || 0 }) };
      const { data } = await api.post("/markings/pindah", body);
      toast.success(`${data.moved} marking dipindah ke ${data.spk.no} · ${data.spk.vendorName}${data.created ? " (SPK baru dibuat)" : ""}`);
      onMoved?.(data); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const tabCls = (on) => `flex-1 rounded border px-3 py-1.5 text-xs ${on ? "border-sky-500/60 bg-sky-500/10 text-sky-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0D1524] border-slate-800 sm:max-w-lg z-[95]" data-testid="spk-move-dialog">
        <DialogHeader><DialogTitle className="font-display text-base text-slate-100 inline-flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-sky-400" />Pindah Marking ke Vendor Lain <span className="cn text-xs font-normal text-slate-500">构件转移至其他供应商</span></DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded border border-slate-800 bg-[#0B1322] p-2 text-xs">
            <div className="text-slate-400">Dari SPK <span className="font-mono text-sky-300">{spk.no}</span> · {spk.vendorName}</div>
            <div className="mt-1 flex flex-wrap gap-1" data-testid="spk-move-codes">{movable.map((m) => <span key={m.id} className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[11px] text-amber-300">{m.code} ×{m.qty}</span>)}</div>
            {locked.length > 0 && <div className="mt-1 text-[11px] text-rose-300" data-testid="spk-move-locked">Tidak ikut dipindah (sudah ada STB/Delivery): {locked.map((m) => m.code).join(", ")}</div>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("spk")} className={tabCls(mode === "spk")} disabled={!spks.length} data-testid="spk-move-mode-spk">Ke SPK yang sudah ada ({spks.length})</button>
            <button type="button" onClick={() => setMode("vendor")} className={tabCls(mode === "vendor")} data-testid="spk-move-mode-vendor">Vendor baru → SPK baru</button>
          </div>
          {mode === "spk" ? (
            <Select value={targetSpk} onValueChange={setTargetSpk}>
              <SelectTrigger className="border-slate-800 bg-slate-900/70" data-testid="spk-move-target-spk"><SelectValue placeholder="Pilih SPK tujuan (proyek sama)" /></SelectTrigger>
              <SelectContent className="bg-[#0D1524] border-slate-800 max-h-64 z-[100]">
                {spks.map((s) => <SelectItem key={s.id} value={s.id}><span className="font-mono">{s.no}</span> · {s.vendorSingkatan || s.vendorName} · {s.qty} pcs</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid grid-cols-[1fr_130px_130px] gap-2">
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger className="border-slate-800 bg-slate-900/70" data-testid="spk-move-vendor"><SelectValue placeholder="Pilih vendor tujuan" /></SelectTrigger>
                <SelectContent className="bg-[#0D1524] border-slate-800 max-h-64 z-[100]">
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.singkatan ? `${v.singkatan} · ` : ""}{v.nama}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="border-slate-800 bg-slate-900/70 text-xs" data-testid="spk-move-tanggal" />
              <Input type="number" min="0" value={rateTon} onChange={(e) => setRateTon(e.target.value)} placeholder="Harga/Ton" className="border-slate-800 bg-slate-900/70 text-xs" data-testid="spk-move-rate" />
            </div>
          )}
          <div className="text-[11px] text-slate-500">Progres QC ikut terbawa; Delivery List proyek otomatis menunjukkan SPK & vendor baru. Total kedua SPK dihitung ulang.</div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700" data-testid="spk-move-cancel">Batal</Button>
            <Button type="button" onClick={submit} disabled={busy || !movable.length} className="bg-sky-600 hover:bg-sky-500" data-testid="spk-move-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Pindahkan {movable.length} marking</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
